import { openMessageFile, canUseDesktopEditors } from '@/constants/utils/files';
import React, { useState, useMemo, useCallback } from 'react';
import { StyleSheet, Pressable, Platform, View, ActivityIndicator } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import PropTypes from 'prop-types';
import { useLightboxStore, useToastStore, useModalStore } from '@/store';
import { Image } from 'expo-image';
import { downloadMedia, isIOSSafari } from '@/constants/utils/mediaDownload';
import { useTranslation } from '@/hooks/useTranslation';
import MediaImagePreview from '@/components/Media/MediaImagePreview';

const createStyles = (colors, isXS, imageCount, isSmall) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    ...(imageCount > 1 && !isSmall && {
      padding: 8,
    }),
  },
  singleImage: {
    width: isSmall ? 80 : isXS ? 300 : 440,
    height: isSmall ? 80 : isXS ? 300 : 440,
    ...(Platform.OS === 'web'
      ? { maxWidth: '90vw', maxHeight: '90vw' }
      : { maxWidth: '100%' }),
    borderRadius: isSmall ? 12 : 18,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    position: 'relative',
  },
  multipleImagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  multipleImage: {
    width: isSmall ? 80 : 210,
    height: isSmall ? 80 : 210,
    borderRadius: 12,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    position: 'relative',
  },
  hoverOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    opacity: 0,
    ...(Platform.OS === 'web' && { transition: 'opacity 0.2s ease' }),
    zIndex: 2,
  },
  imageContainer: {
    position: 'relative',
    ':hover': Platform.OS === 'web' ? {
      '& > div': {
        opacity: 1,
      },
      '& > button': {
        opacity: 1,
      }
    } : {},
  },
  image: {
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
});

const ExpiredImage = React.memo(({ style, styles }) => (
  <View style={[style, styles.imageContainer]}>
    <View style={styles.imageWrapper}>
      <Image
        source={require('@/assets/images/expired-image.png')}
        style={styles.image}
        contentFit="cover"
      />
    </View>
  </View>
));

ExpiredImage.propTypes = {
  style: PropTypes.object.isRequired,
};

const downloadButtonStyle = StyleSheet.create({
  downloadButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.2s ease',
      cursor: 'pointer',
    }),
    zIndex: 3,
  },
});

const DownloadButton = React.memo(({ isHovered, onPress, isLoading, label }) => (
  <Pressable 
    style={[
      downloadButtonStyle.downloadButton,
      (Platform.OS === 'web' && isHovered) && { opacity: 1 },
      (Platform.OS !== 'web') && { opacity: 1 },
    ]}
    onPress={onPress}
    disabled={isLoading}
    accessibilityRole="button"
    accessibilityLabel={label}
  >
    {isLoading ? (
      <ActivityIndicator size="small" color="#fff" />
    ) : (
      <Icon name="arrow-down-to-line" size={18} color="#fff" />
    )}
  </Pressable>
));

DownloadButton.propTypes = {
  isHovered: PropTypes.bool.isRequired,
  onPress: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

const ImageWithOverlay = React.memo(({ image, conversationId, style, styles, openLightbox, handleDownload, isXS }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imageUrl = image.url || image.cdnPath || image.uri;
  const imageName = image.name || image.filename || 'image';
  const isExpired = image.flags?.isExpired || !imageUrl;

  const onDownloadPress = useCallback(async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    await handleDownload(imageUrl);
    setIsDownloading(false);
  }, [handleDownload, imageUrl]);

  if (isExpired || hasError) {
    return <ExpiredImage style={style} styles={styles} />;
  }

  return (
    <View 
      style={[style, styles.imageContainer]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Pressable 
        style={styles.imageWrapper}
        accessibilityRole="button"
        accessibilityLabel={`Open ${imageName}`}
        onPress={() => {
          if (canUseDesktopEditors()) {
            openMessageFile({ ...image, fileType: 'image', cdnPath: imageUrl, ...(conversationId ? { conversationId } : {}) });
          } else {
            openLightbox({ imageUrl, fileId: image.id || image._id });
          }
        }}
      >
        <MediaImagePreview
          uri={imageUrl}
          file={image}
          style={styles.image}
          contentFit="cover"
          transition={200}
          preferThumbnail
          onError={() => setHasError(true)}
        />
      </Pressable>
      <View 
        pointerEvents="none"
        style={[
          styles.hoverOverlay,
          Platform.OS === 'web' && isHovered && !isXS && { opacity: 1 }
        ]} 
      />
      <DownloadButton 
        isHovered={isHovered}
        onPress={onDownloadPress}
        isLoading={isDownloading}
        label={`Download ${imageName}`}
      />
    </View>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.image.id === nextProps.image.id &&
    prevProps.image._id === nextProps.image._id &&
    prevProps.image.url === nextProps.image.url &&
    prevProps.image.cdnPath === nextProps.image.cdnPath &&
    prevProps.image.uri === nextProps.image.uri &&
    prevProps.image.name === nextProps.image.name &&
    prevProps.image.thumbnailPath === nextProps.image.thumbnailPath &&
    prevProps.image.flags?.isExpired === nextProps.image.flags?.isExpired &&
    prevProps.style === nextProps.style
  );
});

ImageWithOverlay.propTypes = {
  image: PropTypes.shape({
    url: PropTypes.string,
    cdnPath: PropTypes.string,
    uri: PropTypes.string,
    name: PropTypes.string,
    filename: PropTypes.string,
    image: PropTypes.any,
    flags: PropTypes.shape({
      isExpired: PropTypes.bool,
    }),
  }).isRequired,
  style: PropTypes.object.isRequired,
  openLightbox: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
};

function MessageImages({ images = [], conversationId, isSmall = false }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const { openLightbox } = useLightboxStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const imageCount = images?.length || 0;

  const styles = useMemo(() => 
    createStyles(colors, isXS, imageCount, isSmall),
    [colors, isXS, imageCount, isSmall]
  );

  const handleDownload = useCallback(async (imageUrl) => {
    if (!imageUrl || imageUrl.includes('expired-image.png')) return;

    const filename = `image-${Date.now()}`;

    // Use the unified downloadMedia function (blob download on web, Photos on native)
    const result = await downloadMedia(imageUrl, filename, {
      onSuccess: (message) => {
        const toastMsg = Platform.OS === 'web'
          ? t('media.downloadComplete')
          : (message === 'Saved to Photos' ? t('media.savedToPhotos') : t('media.downloadComplete'));
        addToast?.(toastMsg, 'success');
      },
      onError: (message) => addToast?.(message || t('media.downloadFailed'), 'error'),
    });

    // If iOS Safari needs fallback, show the instructions modal
    if (result?.needsFallback && Platform.OS === 'web' && isIOSSafari()) {
      openModal('SaveImageInstructionsModal', { imageUrl });
    }
  }, [addToast, t, openModal]);

  if (!images || !Array.isArray(images) || images.length === 0) return null;

  const renderImages = () => {
    if (imageCount === 1) {
      return (
        <ImageWithOverlay 
          image={images[0]}
          conversationId={conversationId}
          styles={styles}
          style={styles.singleImage} 
          openLightbox={openLightbox}
          handleDownload={handleDownload}
          isXS={isXS}
        />
      );
    }

    return (
      <View style={styles.multipleImagesContainer}>
        {images.slice(0, 4).map((image, index) => (
          <ImageWithOverlay
            key={index}
            image={image}
            conversationId={conversationId}
            styles={styles}
            style={styles.multipleImage}
            openLightbox={openLightbox}
            handleDownload={handleDownload}
            isXS={isXS}
          />
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderImages()}
    </View>
  );
}

MessageImages.propTypes = {
  isSmall: PropTypes.bool,
  conversationId: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.any,
    flags: PropTypes.shape({
      isExpired: PropTypes.bool,
    }),
  })),
};

export default React.memo(MessageImages);
